import { useEffect, useState, useCallback } from 'react';
import { APP_VERSION, APP_COMMIT_SHA } from './version';

// Polling de build remota. A cada N min, faz fetch em /version.json
// (escrito pelo scripts/build.js no diretorio build/). Se a build
// publicada for diferente da que esta rodando no browser, sinaliza que
// o operador deveria recarregar pra pegar o codigo novo.
//
// DETECCAO POR commitSha (nao por version): num CCO, todo push em main
// deploya codigo novo, mas o NUMERO da versao (package.json) so muda
// quando se roda `npm run release`. Comparar so a version deixaria o
// operador no cache antigo entre releases, mesmo apos varios deploys.
// O commitSha muda a cada build, entao detecta qualquer atualizacao.
// version ainda e exposta pra o banner rotular "nova versao" vs
// "atualizacao" (rebuild de mesma versao).
//
// Por que isso e critico: operadores deixam o CCO aberto durante
// turnos de 12h+. Sem este check, continuam usando codigo antigo do
// cache do browser indefinidamente, mesmo apos varios deploys.
//
// Dev (APP_VERSION === 'dev'): hook fica desabilitado — nao polla nem
// dispara banner. Sem isso, em desenvolvimento o banner viveria
// permanente, ja que nunca ha version.json local servido pelo CRA dev
// server (alem do que tem em public/, que e estatico).

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5min
const FIRST_CHECK_DELAY_MS = 30 * 1000; // 30s apos load (deixa o app montar)

export const useVersionPoll = () => {
  // update: { version, commitSha, versionChanged } | null
  const [update, setUpdate] = useState(null);

  useEffect(() => {
    // Em dev nao tem version.json gerado, nao faz sentido pollar.
    if (APP_VERSION === 'dev') return undefined;

    let cancelled = false;

    const check = async () => {
      try {
        // Cache-bust no proprio fetch — alguns proxys/nginx ignoram
        // Cache-Control e servem o version.json antigo. Query string
        // unica garante request fresca.
        const res = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;

        // Sinal primario: commitSha mudou = build nova publicada.
        // Fallback: se o version.json nao trouxer commitSha (build
        // antiga), cai pra comparacao de version.
        const remoteSha =
          typeof data.commitSha === 'string' ? data.commitSha : null;
        const remoteVersion =
          typeof data.version === 'string' ? data.version : null;

        const changed = remoteSha
          ? remoteSha !== APP_COMMIT_SHA
          : remoteVersion && remoteVersion !== APP_VERSION;

        if (changed) {
          setUpdate({
            version: remoteVersion || APP_VERSION,
            commitSha: remoteSha,
            versionChanged: !!remoteVersion && remoteVersion !== APP_VERSION,
          });
        }
      } catch (_) {
        // Falha silenciosa. Pode ser rede offline, version.json fora
        // do ar, etc. Sera retentado no proximo tick.
      }
    };

    const firstTimer = setTimeout(check, FIRST_CHECK_DELAY_MS);
    const intervalTimer = setInterval(check, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(firstTimer);
      clearInterval(intervalTimer);
    };
  }, []);

  const reload = useCallback(() => {
    // window.location.reload() em CRA serve a versao nova porque o
    // index.html nao e cacheado (Cache-Control: no-cache no nginx) e
    // os assets em /static/ tem hash no nome (mudaram entre builds).
    window.location.reload();
  }, []);

  return { update, reload };
};
